import { Button } from "@/components/ui/button";
import {
  feedbackNudgeDismissedUntilAtom,
  pageViewsCountAtom,
} from "@/state/feedbackNudge.state";
import { Trans } from "@lingui/react/macro";
import { useAtom, useAtomValue } from "jotai";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const DISMISS_DAYS = 7;
const SHOW_DELAY_MS = 8000;
const MIN_PAGE_VIEW = 20;

function nowMs() {
  return Date.now();
}

function daysToMs(days: number) {
  return days * 24 * 60 * 60 * 1000;
}

export type FeedbackNudgeVariant = "card" | "thin";

type Props = {
  variant?: FeedbackNudgeVariant;
};

export function FeedbackNudge({ variant = "card" }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [dismissedUntil, setDismissedUntil] = useAtom(
    feedbackNudgeDismissedUntilAtom,
  );
  const pageViews = useAtomValue(pageViewsCountAtom);

  const [ready, setReady] = useState(false);

  const excluded = useMemo(() => {
    const path = location.pathname;
    return (
      path === "/feedback" || path === "/auth" || path === "/strava/popup-close"
    );
  }, [location.pathname]);

  const isDismissed = useMemo(() => {
    if (dismissedUntil == null) return false;
    return dismissedUntil > nowMs();
  }, [dismissedUntil]);

  const hasSeenEnoughPages = pageViews >= MIN_PAGE_VIEW;

  useEffect(() => {
    if (excluded || isDismissed) return;

    const t = window.setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [excluded, isDismissed]);

  const openFeedback = () => {
    setDismissedUntil(nowMs() + daysToMs(DISMISS_DAYS));
    navigate("/feedback");
  };

  const dismiss = () => {
    setDismissedUntil(nowMs() + daysToMs(DISMISS_DAYS));
  };

  if (excluded || isDismissed || !ready || !hasSeenEnoughPages) return null;

  if (variant === "thin") {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50">
        <div className="border-t border-gray-200 bg-white/95 backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95">
          <div className="container mx-auto flex items-center gap-3 px-4 py-2">
            <div className="min-w-0 flex-1 truncate text-xs text-gray-700 dark:text-gray-200">
              <span className="font-medium">
                <Trans>We just launched 🎉</Trans>
              </span>{" "}
              <span className="text-gray-600 dark:text-gray-300">
                <Trans>
                  We’d love your feedback to make Stratistics better.
                </Trans>
              </span>
            </div>

            <Button
              onClick={openFeedback}
              size="sm"
              className="h-7 px-3 text-xs bg-gradient-to-r from-orange-600 to-red-600 text-white shadow hover:from-orange-500 hover:to-red-500 focus-visible:ring-orange-500"
            >
              <Trans>Give feedback</Trans>
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={dismiss}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-1/2 z-50 max-w-lg -translate-x-1/2">
      <div className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white/95 p-4 shadow-lg backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            <Trans>We just launched 🎉</Trans>
          </div>
          <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            <Trans>We’d love your feedback to make Stratistics better.</Trans>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              onClick={openFeedback}
              className="bg-gradient-to-r from-orange-600 to-red-600 text-white shadow hover:from-orange-500 hover:to-red-500 focus-visible:ring-orange-500"
              size="sm"
            >
              <Trans>Give feedback</Trans>
            </Button>

            <Button onClick={dismiss} variant="outline" size="sm">
              <Trans>Not now</Trans>
            </Button>
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="-mr-1 -mt-1 h-8 w-8"
          onClick={dismiss}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
